import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import api from '../../utils/axios';
import Swal from 'sweetalert2';
import { formatPrice } from '../../utils/priceFormatter';
import { API_BASE, buildAssetUrl } from '../../utils/url';
import { FaEye } from 'react-icons/fa';

const AdminProducts = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [currentProduct, setCurrentProduct] = useState(null);

  // CSV‐related state
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [csvFile, setCsvFile] = useState(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Frozen quantities modal
  const [isFrozenModalOpen, setIsFrozenModalOpen] = useState(false);
  const [selectedProductFrozen, setSelectedProductFrozen] = useState(null);
  const [editingExpiryId, setEditingExpiryId] = useState(null);
  const [editingExpiryDate, setEditingExpiryDate] = useState('');
  // Extend/Modify modals
  const [isExtendModalOpen, setIsExtendModalOpen] = useState(false);
  const [extendTarget, setExtendTarget] = useState(null); // frozen quantity record
  const [extendDate, setExtendDate] = useState('');
  const [isModifyModalOpen, setIsModifyModalOpen] = useState(false);
  const [modifyTarget, setModifyTarget] = useState(null);
  const [modifyQty, setModifyQty] = useState('');

  // Filter/search UI
  const [searchText, setSearchText] = useState('');
  const [filterCategory, setFilterCategory] = useState('all');
  const [filterStockStatus, setFilterStockStatus] = useState('all'); // all, out_of_stock, frozen
  const [sortStock, setSortStock] = useState('none'); // none, low_to_high, high_to_low

  // Form data state
  const [formData, setFormData] = useState({
    product_id: '',
    name: '',
    short_description: '',
    images: [],
    categoryIds: [],
    price: '',
    inventory: '',
    freeze_qty: '',
    status: 'active',
    botanical_name: '',
    origin: '',
    collection: '',
    grain: '',
    grain_direction: '',
    dimension: '',
    veneer_thickness: '',
    finish: '',
    substrate: '',
    shade: '',
    color: ''
  });

  // Pagination
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    itemsPerPage: 50
  });

  const [selectedProducts, setSelectedProducts] = useState([]);

  useEffect(() => {
    fetchCategories();
    fetchProducts();
    const editId = searchParams.get('edit');
    if (editId) fetchProductForEdit(editId);
  }, [searchParams]);

  // Fetch all categories
  const fetchCategories = async () => {
    try {
      const { data } = await api.get('/categories');
      setCategories(data);
    } catch (error) {
      Swal.fire('Error', 'Failed to load categories', 'error');
    }
  };

  // Fetch all products
  const fetchProducts = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get('/products');
      const totalPages = Math.ceil(data.length / pagination.itemsPerPage);
      setProducts(data);
      setPagination((p) => ({
        ...p,
        currentPage: 1,
        totalPages
      }));
    } catch (error) {
      Swal.fire('Error', 'Failed to load products', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle viewing frozen quantity details
  const handleViewFrozenDetails = (product) => {
    setSelectedProductFrozen(product);
    setIsFrozenModalOpen(true);
  };

  // Handle editing expiry date
  const handleEditExpiryDate = (frozenQuantity) => {
    setEditingExpiryId(frozenQuantity.id);
    // Format date for input (YYYY-MM-DDTHH:MM)
    const date = new Date(frozenQuantity.expires_at);
    const formattedDate = date.toISOString().slice(0, 16);
    setEditingExpiryDate(formattedDate);
  };

  // Open extend popup
  const openExtendModal = (fq) => {
    setExtendTarget(fq);
    const date = new Date(fq.expires_at);
    setExtendDate(date.toISOString().slice(0, 16));
    setIsExtendModalOpen(true);
  };
  const saveExtend = async () => {
    try {
      await api.put(`/frozen-quantities/update-expiry/${extendTarget.id}`, { expires_at: extendDate });
      Swal.fire({ icon: 'success', title: 'Extended', timer: 1200, showConfirmButton: false });
      setIsExtendModalOpen(false);
      setExtendTarget(null);
      setExtendDate('');
      await fetchProducts();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Failed to extend', text: error.response?.data?.message || 'Error', confirmButtonColor: '#000' });
    }
  };

  // Open modify popup
  const openModifyModal = (fq) => {
    setModifyTarget(fq);
    setModifyQty(String(fq.quantity || ''));
    setIsModifyModalOpen(true);
  };
  const saveModify = async () => {
    try {
      const qtyNum = parseInt(modifyQty, 10);
      if (!qtyNum || qtyNum <= 0) {
        return Swal.fire({ icon: 'error', title: 'Invalid quantity', timer: 1200, showConfirmButton: false });
      }
      await api.put(`/frozen-quantities/update-quantity/${modifyTarget.id}`, { quantity: qtyNum });
      Swal.fire({ icon: 'success', title: 'Quantity updated', timer: 1200, showConfirmButton: false });
      setIsModifyModalOpen(false);
      setModifyTarget(null);
      setModifyQty('');
      await fetchProducts();
    } catch (error) {
      Swal.fire({ icon: 'error', title: 'Failed to update', text: error.response?.data?.message || 'Error', confirmButtonColor: '#000' });
    }
  };

  // Handle saving expiry date
  const handleSaveExpiryDate = async () => {
    try {
      if (!editingExpiryDate) {
        Swal.fire('Error', 'Please select a valid date', 'error');
        return;
      }

      const response = await api.put(`/frozen-quantities/update-expiry/${editingExpiryId}`, {
        expires_at: editingExpiryDate
      });

      if (response.data) {
        Swal.fire('Success', 'Expiry date updated successfully', 'success');

        // Refresh the product data to show updated expiry date
        await fetchProducts();

        // Reset editing state
        setEditingExpiryId(null);
        setEditingExpiryDate('');
      }
    } catch (error) {
      console.error('Error updating expiry date:', error);
      Swal.fire('Error', error.response?.data?.message || 'Failed to update expiry date', 'error');
    }
  };

  // Handle canceling edit
  const handleCancelEdit = () => {
    setEditingExpiryId(null);
    setEditingExpiryDate('');
  };

  // Filtering logic
  let filteredProducts = products.filter((p) => {
    const matchCat =
      filterCategory === 'all' ||
      (p.categories && p.categories.some((c) => c.id === Number(filterCategory)));
    const matchSearch =
      !searchText ||
      p.name.toLowerCase().includes(searchText.toLowerCase()) ||
      (p.product_id && p.product_id.toLowerCase().includes(searchText.toLowerCase()));
    
    // Stock Status filter
    let matchStock = true;
    if (filterStockStatus === 'out_of_stock') {
      matchStock = (p.inventory || 0) <= 0;
    } else if (filterStockStatus === 'frozen') {
      matchStock = (p.frozen_quantity || 0) > 0;
    }

    return matchCat && matchSearch && matchStock;
  });

  // Sorting logic
  if (sortStock === 'low_to_high') {
    filteredProducts.sort((a, b) => (a.inventory || 0) - (b.inventory || 0));
  } else if (sortStock === 'high_to_low') {
    filteredProducts.sort((a, b) => (b.inventory || 0) - (a.inventory || 0));
  }

  // Pagination helpers
  const handlePageChange = (page) => {
    if (page < 1 || page > Math.ceil(filteredProducts.length / pagination.itemsPerPage)) return;
    setPagination((p) => ({ ...p, currentPage: page }));
  };
  const getCurrentProducts = () => {
    const start = (pagination.currentPage - 1) * pagination.itemsPerPage;
    const end = start + pagination.itemsPerPage;
    return filteredProducts.slice(start, end);
  };

  // --- CRUD + Modal Handlers (same as your code, unchanged) ---

  // Fetch a single product for editing (and open the modal)
  const fetchProductForEdit = async (productId) => {
    try {
      const { data } = await api.get(`/products/${productId}`);
      // Normalize images
      let imagesArray = [];
      if (Array.isArray(data.images)) {
        imagesArray = data.images;
      } else if (typeof data.images === 'string') {
        try {
          const parsed = JSON.parse(data.images);
          if (Array.isArray(parsed)) imagesArray = parsed;
          else imagesArray = data.images.split(',').map((img) => img.trim()).filter((img) => img);
        } catch {
          imagesArray = data.images.split(',').map((img) => img.trim()).filter((img) => img);
        }
      }
      // Build category IDs array:
      const categoryIdsArray = (data.categories || []).map((c) => c.id);
      setCurrentProduct(data);
      setFormData({
        product_id: data.product_id || '',
        name: data.name || '',
        short_description: data.short_description || '',
        description: data.description || '',
        images: imagesArray,
        categoryIds: categoryIdsArray,
        price: data.price || '',
        inventory: data.inventory || '',
        freeze_qty: data.freeze_qty || '',
        status: data.status || 'active',
        botanical_name: data.botanical_name || '',
        origin: data.origin || '',
        collection: data.collection || '',
        grain: data.grain || '',
        grain_direction: data.grain_direction || '',
        dimension: data.dimension || '',
        veneer_thickness: data.veneer_thickness || '',
        finish: data.finish || '',
        substrate: data.substrate || '',
        shade: data.shade || '',
        color: data.color || ''
      });
      setEditMode(true);
      setIsModalOpen(true);
    } catch (error) {
      Swal.fire('Error', 'Failed to load product details', 'error');
    }
  };

  const openAddModal = () => {
    setFormData({
      product_id: '', name: '', short_description: '', images: [],
      categoryIds: [], price: '', inventory: '', freeze_qty: '', status: 'active',
      botanical_name: '', origin: '', collection: '',
      grain: '', grain_direction: '', dimension: '', veneer_thickness: '', finish: '',
      substrate: '', shade: '', color: ''
    });
    setCurrentProduct(null);
    setEditMode(false);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setCurrentProduct(null);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name === 'categoryIds') {
      setFormData((fd) => ({
        ...fd,
        categoryIds: value ? [parseInt(value, 10)] : []
      }));
      return;
    }
    setFormData((fd) => ({
      ...fd,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleImageUpload = async (e) => {
    if (!e.target.files || e.target.files.length === 0) return;

    console.log('Uploading files:', e.target.files);

    try {
      const uploadForm = new FormData();
      Array.from(e.target.files).forEach((file) => {
        console.log('Adding file to FormData:', file.name, file.type, file.size);
        uploadForm.append('files', file);
      });

      console.log('FormData created, sending to server...');
      const { data } = await api.post('/upload/multiple', uploadForm, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      });
      console.log('Upload response:', data);

      const newImages = data.files.map((f) => f.path);
      console.log('New images paths:', newImages);

      setFormData((fd) => ({
        ...fd,
        images: [...fd.images, ...newImages]
      }));

      Swal.fire({
        icon: 'success',
        title: 'Images uploaded',
        timer: 1200,
        showConfirmButton: false
      });
    } catch (error) {
      console.error('Upload error:', error);
      console.error('Upload error response:', error.response?.data);
      Swal.fire('Error', 'Failed to upload images', 'error');
    }
  };

  const handleRemoveImage = (index) => {
    setFormData((fd) => {
      const arr = [...fd.images];
      arr.splice(index, 1);
      return { ...fd, images: arr };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      return Swal.fire('Validation Error', 'Product name is required.', 'error');
    }
    if (!formData.images || formData.images.length === 0) {
      return Swal.fire('Validation Error', 'At least one image is required.', 'error');
    }
    try {
      const payload = {
        product_id: formData.product_id,
        name: formData.name.trim(),
        short_description: formData.short_description,
        images: formData.images, // Send as array, not string
        price: parseFloat(formData.price) || 0.00,
        inventory: parseInt(formData.inventory) || 0,
        freeze_qty: parseInt(formData.freeze_qty) || 0,
        status: formData.status,
        botanical_name: formData.botanical_name,
        origin: formData.origin,
        collection: formData.collection,
        grain: formData.grain,
        grain_direction: formData.grain_direction,
        dimension: formData.dimension,
        veneer_thickness: formData.veneer_thickness,
        finish: formData.finish,
        substrate: formData.substrate,
        shade: formData.shade,
        color: formData.color,
        categoryIds: formData.categoryIds
      };

      // Debug: Log the payload being sent
      console.log('Sending payload:', payload);
      console.log('Images array:', formData.images);

      let successMessage = '';
      if (editMode && currentProduct) {
        await api.put(`/products/${currentProduct.id}`, payload);
        successMessage = 'Product updated successfully';
      } else {
        await api.post('/products', payload);
        successMessage = 'Product created successfully';
      }
      Swal.fire({
        icon: 'success',
        title: successMessage,
        timer: 1200,
        showConfirmButton: false
      });
      closeModal();
      fetchProducts();
    } catch (error) {
      console.error('Product submission error:', error);
      console.error('Error response:', error.response?.data);
      Swal.fire(
        'Error',
        error.response?.data?.message || `Error updating Product`,
        'error'
      );
    }
  };

  const handleDelete = (product) => {
    Swal.fire({
      title: `Delete “${product.name}”?`,
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete it!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete(`/products/${product.id}`);
          Swal.fire({
            icon: 'success',
            title: 'Deleted',
            timer: 1200,
            showConfirmButton: false
          });
          fetchProducts();
        } catch {
          Swal.fire('Error', 'Failed to delete product', 'error');
        }
      }
    });
  };

  const handleSelectProduct = (id) => {
    setSelectedProducts(prev => 
      prev.includes(id) ? prev.filter(pid => pid !== id) : [...prev, id]
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = getCurrentProducts().map(p => p.id);
      setSelectedProducts(allIds);
    } else {
      setSelectedProducts([]);
    }
  };

  const handleBulkDelete = () => {
    if (selectedProducts.length === 0) return;

    Swal.fire({
      title: `Delete ${selectedProducts.length} Products?`,
      text: 'This action cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete selected!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete('/products/delete-bulk', { data: { ids: selectedProducts } });
          Swal.fire({
            icon: 'success',
            title: 'Deleted',
            timer: 1200,
            showConfirmButton: false
          });
          setSelectedProducts([]);
          fetchProducts();
        } catch (error) {
          Swal.fire('Error', error.response?.data?.message || 'Failed to delete products', 'error');
        }
      }
    });
  };

  // CSV import
  const openImportModal = () => { setCsvFile(null); setIsImportModalOpen(true); };
  const closeImportModal = () => { setCsvFile(null); setIsImportModalOpen(false); };
  const handleCsvFileChange = (e) => {
    setCsvFile(e.target.files && e.target.files.length > 0 ? e.target.files[0] : null);
  };
  const handleCsvImport = async (e) => {
    e.preventDefault();
    if (!csvFile) {
      return Swal.fire('Validation Error', 'Please select a CSV file.', 'error');
    }
    setIsImporting(true);
    const form = new FormData();
    form.append('csvFile', csvFile);
    try {
      const { data } = await api.post(
        '/products/import-csv',
        form,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );
      Swal.fire({ icon: 'success', title: 'Import Complete', html: `<p>${data.message}</p>`, confirmButtonColor: '#000' });
      closeImportModal();
      fetchProducts();
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Failed to import CSV', 'error');
    } finally {
      setIsImporting(false);
    }
  };

  const handleCsvExport = async () => {
    setIsExporting(true);
    try {
      const response = await api.get('/products/export-csv', {
        responseType: 'blob', // Important for file downloads
      });

      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;

      // Get filename from response headers or use default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'products-export.csv';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="(.+)"/);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      }

      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      Swal.fire({
        icon: 'success',
        title: 'Export Complete',
        text: 'Products exported successfully!',
        confirmButtonColor: '#000'
      });
    } catch (error) {
      Swal.fire('Error', error.response?.data?.message || 'Failed to export CSV', 'error');
    } finally {
      setIsExporting(false);
    }
  };

  const handleDeleteAllProducts = () => {
    Swal.fire({
      title: 'Delete ALL Products?',
      text: 'This action will wipe ALL products from the database! This cannot be undone.',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, DELETE ALL!'
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await api.delete('/products/delete-all');
          Swal.fire({
            icon: 'success',
            title: 'All Products Deleted',
            timer: 1500,
            showConfirmButton: false
          });
          fetchProducts();
        } catch (error) {
          console.error("Delete all error", error);
          Swal.fire('Error', error.response?.data?.message || 'Failed to delete products', 'error');
        }
      }
    });
  };

  // -------------------------- UI --------------------------
  return (
    <div className="p-6">
      {/* Header/Filters */}
      {/* Header section with Title and Action Buttons */}
      <div className="flex flex-col space-y-4 mb-6">
        <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-4">
          <h1 className="text-2xl font-semibold text-gray-800">Products</h1>
          
          <div className="flex flex-wrap gap-2">
            {selectedProducts.length > 0 && (
              <button
                onClick={handleBulkDelete}
                className="px-3 py-1.5 bg-red-100 text-red-700 text-sm font-medium rounded border border-red-200 hover:bg-red-200 transition flex items-center gap-1"
              >
                Delete Selected ({selectedProducts.length})
              </button>
            )}

            <button
              onClick={openAddModal}
              className="px-3 py-1.5 bg-black text-white text-sm font-medium rounded hover:bg-gray-800 transition"
            >
              + Add Product
            </button>

            <button
              onClick={openImportModal}
              className="px-3 py-1.5 bg-gray-700 text-white text-sm font-medium rounded hover:bg-gray-600 transition"
            >
              Import CSV
            </button>

            <button
              onClick={handleCsvExport}
              disabled={isExporting}
              className="px-3 py-1.5 bg-green-600 text-white text-sm font-medium rounded hover:bg-green-700 disabled:bg-green-400 transition flex items-center gap-2"
            >
              {isExporting ? (
                <>
                  <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                  <span>Exporting...</span>
                </>
              ) : (
                'Export CSV'
              )}
            </button>

            <button
              onClick={handleDeleteAllProducts}
              className="px-3 py-1.5 bg-white text-red-600 border border-red-200 text-sm font-medium rounded hover:bg-red-50 transition"
            >
              Delete All
            </button>
          </div>
        </div>

        {/* Filter Bar */}
        <div className="flex flex-wrap items-center gap-3 bg-white p-3 rounded-lg border border-gray-200 shadow-sm transition-all">
          <div className="flex items-center gap-2 min-w-[150px]">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Category</span>
            <select
              className="border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-gray-200 outline-none w-full"
              value={filterCategory}
              onChange={e => { setFilterCategory(e.target.value); setPagination(p => ({ ...p, currentPage: 1 })); }}
            >
              <option value="all">All Categories</option>
              {categories.map((cat) => (
                <option value={cat.id} key={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>

          <div className="flex items-center gap-2 min-w-[150px]">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Stock</span>
            <select
              className="border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-gray-200 outline-none w-full"
              value={filterStockStatus}
              onChange={e => { setFilterStockStatus(e.target.value); setPagination(p => ({ ...p, currentPage: 1 })); }}
            >
              <option value="all">All Status</option>
              <option value="out_of_stock">Out of Stock</option>
              <option value="frozen">Has Frozen Stock</option>
            </select>
          </div>

          <div className="flex items-center gap-2 min-w-[150px]">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Sort</span>
            <select
              className="border border-gray-300 rounded px-2 py-1.5 text-sm focus:ring-2 focus:ring-gray-200 outline-none w-full"
              value={sortStock}
              onChange={e => { setSortStock(e.target.value); }}
            >
              <option value="none">No Sorting</option>
              <option value="low_to_high">Stock: Low to High</option>
              <option value="high_to_low">Stock: High to Low</option>
            </select>
          </div>

          <div className="flex-grow flex items-center gap-2 min-w-[200px]">
            <span className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Search</span>
            <div className="relative w-full">
              <input
                type="text"
                placeholder="Search by name or ID..."
                className="border border-gray-300 rounded pl-8 pr-3 py-1.5 text-sm focus:ring-2 focus:ring-gray-200 outline-none w-full"
                value={searchText}
                onChange={e => { setSearchText(e.target.value); setPagination(p => ({ ...p, currentPage: 1 })); }}
              />
              <svg className="w-4 h-4 absolute left-2.5 top-2.5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
              </svg>
            </div>
          </div>
        </div>
      </div>

      {/* Products Table */}
      <div style={{
        background: '#fff',
        borderRadius: '12px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
        overflow: 'hidden'
      }}>
        <div style={{
          overflowX: 'auto'
        }}>
          <table style={{
            width: '100%',
            borderCollapse: 'collapse'
          }}>
            <thead>
              <tr style={{
                background: '#f8f9fa',
                borderBottom: '1px solid #e9ecef'
              }}>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>
                  <input 
                    type="checkbox" 
                    onChange={handleSelectAll} 
                    checked={selectedProducts.length > 0 && selectedProducts.length === getCurrentProducts().length}
                  />
                </th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Product</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Product ID</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Category</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Stock Qty</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Frozen/Available</th>
                <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Status</th>
                <th style={{ padding: '16px', textAlign: 'center', fontWeight: '600' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan="8" style={{ padding: '16px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '8px' }}>
                      <div style={{
                        width: '16px',
                        height: '16px',
                        border: '2px solid #f3f3f3',
                        borderTop: '2px solid #333',
                        borderRadius: '50%',
                        animation: 'spin 1s linear infinite'
                      }}></div>
                      <span>Loading products...</span>
                    </div>
                  </td>
                </tr>
              ) : getCurrentProducts().length === 0 ? (
                <tr>
                  <td colSpan="9" style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                    No products found
                  </td>
                </tr>
              ) : (
                getCurrentProducts().map((prod) => {
                  let imgsArray = [];
                  if (Array.isArray(prod.images)) imgsArray = prod.images;
                  else if (typeof prod.images === 'string') {
                    try {
                      const parsed = JSON.parse(prod.images);
                      if (Array.isArray(parsed)) imgsArray = parsed;
                      else imgsArray = prod.images.split(',').map((str) => str.trim()).filter((str) => str);
                    } catch {
                      imgsArray = prod.images.split(',').map((str) => str.trim()).filter((str) => str);
                    }
                  }
                  const thumbPath = imgsArray.length > 0 ? imgsArray[0] : '';
                  const thumbURL = buildAssetUrl(thumbPath);
                  return (
                    <tr key={prod.id} style={{
                      borderBottom: '1px solid #f1f3f4',
                      backgroundColor: selectedProducts.includes(prod.id) ? '#f0f7ff' : 'transparent'
                    }}>
                      <td style={{ padding: '16px' }}>
                        <input 
                          type="checkbox" 
                          checked={selectedProducts.includes(prod.id)}
                          onChange={() => handleSelectProduct(prod.id)}
                        />
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ display: 'flex', alignItems: 'center' }}>
                          <div style={{ flexShrink: 0, width: '40px', height: '40px', background: '#f3f4f6', borderRadius: '8px', overflow: 'hidden' }}>
                            {thumbURL ? (
                              <img
                                src={thumbURL}
                                alt={prod.name}
                                style={{ width: '100%', height: '100%', objectFit: 'cover' }}
                              />
                            ) : (
                              <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '10px' }}>
                                No Img
                              </div>
                            )}
                          </div>
                          <div style={{ marginLeft: '12px' }}>
                            <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>
                              {prod.name}
                            </div>
                            {prod.botanical_name && (
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                {prod.botanical_name}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {prod.product_id || '—'}
                        </div>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div style={{ fontSize: '12px', color: '#666' }}>
                          {prod.categories && prod.categories.length > 0
                            ? prod.categories.map((c) => c.name).join(', ')
                            : '—'}
                        </div>
                      </td>

                      <td style={{ padding: '16px' }}>
                        <span style={{
                          color: (prod.inventory || 0) > 5 ? "#22c55e" : (prod.inventory || 0) > 0 ? "#ef4444" : "#ef4444",
                          fontWeight: 500
                        }}>
                          {prod.inventory || 0}
                        </span>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <div>
                          <div style={{
                            color: (prod.frozen_quantity || 0) > 0 ? "#f59e0b" : "#6b7280",
                            fontWeight: 500
                          }}>
                            Frozen: {prod.frozen_quantity || 0}
                          </div>
                          <div style={{
                            color: "#22c55e",
                            fontSize: "11px",
                            fontWeight: 400
                          }}>
                            Available: {prod.available_quantity || prod.inventory || 0}
                          </div>
                          {(prod.frozen_quantity || 0) > 0 && prod.frozen_details && (
                            <div style={{ marginTop: "4px", fontSize: "10px", color: "#666" }}>
                              {prod.frozen_details.latest_frozen_at && (
                                <div style={{ marginBottom: "2px" }}>
                                  <span style={{ fontWeight: 500 }}>Frozen:</span> {new Date(prod.frozen_details.latest_frozen_at).toLocaleDateString()} {new Date(prod.frozen_details.latest_frozen_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              )}
                              {prod.frozen_details.latest_expires_at && (
                                <div>
                                  <span style={{ fontWeight: 500 }}>Expires:</span> {new Date(prod.frozen_details.latest_expires_at).toLocaleDateString()} {new Date(prod.frozen_details.latest_expires_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                </div>
                              )}
                              {prod.frozen_details.count > 1 && (
                                <div style={{ marginTop: "2px", color: "#888", fontSize: "9px" }}>
                                  +{prod.frozen_details.count - 1} more freeze(s)
                                </div>
                              )}
                              <button
                                onClick={() => handleViewFrozenDetails(prod)}
                                style={{
                                  marginTop: "4px",
                                  background: "#007bff",
                                  color: "#fff",
                                  border: "none",
                                  borderRadius: "4px",
                                  padding: "2px 6px",
                                  fontSize: "9px",
                                  cursor: "pointer",
                                  fontWeight: 500
                                }}
                              >
                                View Details
                              </button>
                            </div>
                          )}
                        </div>
                      </td>
                      <td style={{ padding: '16px' }}>
                        <span style={{
                          padding: '4px 8px',
                          fontSize: '12px',
                          fontWeight: '500',
                          borderRadius: '12px',
                          background: prod.status === 'active' ? '#dcfce7' : prod.status === 'inactive' ? '#f3f4f6' : '#fecaca',
                          color: prod.status === 'active' ? '#166534' : prod.status === 'inactive' ? '#374151' : '#dc2626'
                        }}>
                          {prod.status ? prod.status.replace('_', ' ').toUpperCase() : 'DRAFT'}
                        </span>
                      </td>
                      <td style={{ padding: '16px', textAlign: 'center' }}>
                        <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                          <button
                            onClick={() => fetchProductForEdit(prod.id)}
                            style={{
                              background: '#3b82f6',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '6px 12px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            Edit
                          </button>
                          <button
                            onClick={() => handleDelete(prod)}
                            style={{
                              background: '#ef4444',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '6px 12px',
                              fontSize: '12px',
                              cursor: 'pointer',
                              fontWeight: '500'
                            }}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination Controls */}
      {!isLoading && filteredProducts.length > 0 && (
        <div className="flex justify-between items-center mt-4">
          <div className="text-sm text-gray-700">
            Showing{' '}
            {(pagination.currentPage - 1) * pagination.itemsPerPage + 1} to{' '}
            {Math.min(pagination.currentPage * pagination.itemsPerPage, filteredProducts.length)} of{' '}
            {filteredProducts.length} products
          </div>
          <div className="space-x-1">
            <button
              onClick={() => handlePageChange(pagination.currentPage - 1)}
              disabled={pagination.currentPage === 1}
              className={`px-3 py-1 rounded ${pagination.currentPage === 1
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              Previous
            </button>
            <button
              onClick={() => handlePageChange(pagination.currentPage + 1)}
              disabled={pagination.currentPage === Math.ceil(filteredProducts.length / pagination.itemsPerPage)}
              className={`px-3 py-1 rounded ${pagination.currentPage === Math.ceil(filteredProducts.length / pagination.itemsPerPage)
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                }`}
            >
              Next
            </button>
          </div>
        </div>
      )}

      {/* Add/Edit Product Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">
                  {editMode ? 'Edit Product' : 'Add New Product'}
                </h2>
                <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">
                  ×
                </button>
              </div>

              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Basic Info */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Name *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded focus:ring-black focus:border-black"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Product ID
                    </label>
                    <input
                      type="text"
                      name="product_id"
                      value={formData.product_id}
                      onChange={handleInputChange}
                      placeholder="Auto-generated if empty"
                      className="w-full p-2 border rounded focus:ring-black focus:border-black"
                    />
                  </div>
                </div>

                {/* E-commerce Fields */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Price (₹) *
                    </label>
                    <input
                      type="number"
                      name="price"
                      value={formData.price}
                      onChange={handleInputChange}
                      step="0.01"
                      min="0"
                      placeholder="Enter price in INR"
                      className="w-full p-2 border rounded focus:ring-black focus:border-black"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Stock Qty *
                    </label>
                    <input
                      type="number"
                      name="inventory"
                      value={formData.inventory}
                      onChange={handleInputChange}
                      min="0"
                      placeholder="Total quantity"
                      className="w-full p-2 border rounded focus:ring-black focus:border-black"
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Freeze Qty
                    </label>
                    <input
                      type="number"
                      name="freeze_qty"
                      value={formData.freeze_qty}
                      onChange={handleInputChange}
                      min="0"
                      placeholder="Reserved quantity"
                      className="w-full p-2 border rounded focus:ring-black focus:border-black"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      name="status"
                      value={formData.status}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded focus:ring-black focus:border-black"
                    >
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                      <option value="out_of_stock">Out of Stock</option>
                    </select>
                  </div>
                </div>
                {/* Description */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Short Description
                  </label>
                  <textarea
                    name="short_description"
                    value={formData.short_description}
                    onChange={handleInputChange}
                    rows={3}
                    className="w-full p-2 border rounded focus:ring-black focus:border-black"
                  />
                </div>
                {/* Product Details */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Category */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Category
                    </label>
                    <select
                      name="categoryIds"
                      value={formData.categoryIds.length > 0 ? formData.categoryIds[0] : ''}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded focus:ring-black focus:border-black"
                    >
                      <option value="">Select Category</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </div>
                  {/* Botanical Name */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Botanical Name
                    </label>
                    <input
                      type="text"
                      name="botanical_name"
                      value={formData.botanical_name}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded focus:ring-black focus:border-black"
                    />
                  </div>
                  {/* Origin */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Origin
                    </label>
                    <input
                      type="text"
                      name="origin"
                      value={formData.origin}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded focus:ring-black focus:border-black"
                    />
                  </div>
                  {/* Collection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Collection
                    </label>
                    <input
                      type="text"
                      name="collection"
                      value={formData.collection}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded focus:ring-black focus:border-black"
                    />
                  </div>
                  {/* Grain */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Grain
                    </label>
                    <input
                      type="text"
                      name="grain"
                      value={formData.grain}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded focus:ring-black focus:border-black"
                    />
                  </div>
                  {/* Direction of Grain */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Direction of Grain
                    </label>
                    <input
                      type="text"
                      name="grain_direction"
                      value={formData.grain_direction}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded focus:ring-black focus:border-black"
                      placeholder="e.g., Horizontal, Vertical, Cross"
                    />
                  </div>
                  {/* Dimension */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Dimension
                    </label>
                    <input
                      type="text"
                      name="dimension"
                      value={formData.dimension}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded focus:ring-black focus:border-black"
                    />
                  </div>
                  {/* Veneer Thickness */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Veneer Thickness
                    </label>
                    <input
                      type="text"
                      name="veneer_thickness"
                      value={formData.veneer_thickness}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded focus:ring-black focus:border-black"
                    />
                  </div>
                  {/* Finish */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Finish
                    </label>
                    <input
                      type="text"
                      name="finish"
                      value={formData.finish}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded focus:ring-black focus:border-black"
                    />
                  </div>
                  {/* Substrate */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Substrate
                    </label>
                    <input
                      type="text"
                      name="substrate"
                      value={formData.substrate}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded focus:ring-black focus:border-black"
                    />
                  </div>
                </div>
                {/* Color & Shade */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Color
                    </label>
                    <input
                      type="text"
                      name="color"
                      value={formData.color}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded focus:ring-black focus:border-black"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Shade
                    </label>
                    <input
                      type="text"
                      name="shade"
                      value={formData.shade}
                      onChange={handleInputChange}
                      className="w-full p-2 border rounded focus:ring-black focus:border-black"
                    />
                  </div>
                </div>
                {/* Images Upload */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Images
                  </label>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 mb-4">
                    {(formData.images || []).map((imgSrc, idx) => {
                      const actualSrc = buildAssetUrl(imgSrc);
                      return (
                        <div key={idx} className="relative group">
                          <img
                            src={actualSrc}
                            alt={`Product ${idx + 1}`}
                            className="w-full h-32 object-cover rounded"
                          />
                          <button
                            type="button"
                            onClick={() => handleRemoveImage(idx)}
                            className="absolute top-2 right-2 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                          >
                            ×
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={handleImageUpload}
                    className="block w-full text-sm text-gray-500
                               file:mr-4 file:py-2 file:px-4 file:rounded
                               file:border-0 file:text-sm file:font-semibold
                               file:bg-black file:text-white hover:file:bg-gray-900"
                  />
                </div>
                {/* Submit Buttons */}
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeModal}
                    className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-black text-white rounded hover:bg-gray-900"
                  >
                    {editMode ? 'Update Product' : 'Add Product'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* CSV Import Modal */}
      {isImportModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">Import Products from CSV</h2>
                <button onClick={closeImportModal} className="text-gray-500 hover:text-gray-700">
                  ×
                </button>
              </div>
              <form onSubmit={handleCsvImport} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    CSV File
                  </label>
                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleCsvFileChange}
                    className="block w-full text-sm text-gray-500
                               file:mr-4 file:py-2 file:px-4 file:rounded
                               file:border-0 file:text-sm file:font-semibold
                               file:bg-black file:text-white hover:file:bg-gray-900"
                  />
                </div>
                <div className="flex justify-end space-x-3">
                  <button
                    type="button"
                    onClick={closeImportModal}
                    className="px-4 py-2 border rounded text-gray-700 hover:bg-gray-50"
                    disabled={isImporting}
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-black text-white rounded hover:bg-gray-900 disabled:bg-gray-400 disabled:cursor-not-allowed"
                    disabled={!csvFile || isImporting}
                  >
                    {isImporting ? (
                      <div className="flex items-center space-x-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
                        <span>Importing...</span>
                      </div>
                    ) : (
                      'Import CSV'
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Frozen Quantities Details Modal */}
      {isFrozenModalOpen && selectedProductFrozen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-xl font-semibold">
                  Frozen Quantities - {selectedProductFrozen.name}
                </h2>
                <button
                  onClick={() => setIsFrozenModalOpen(false)}
                  className="text-gray-500 hover:text-gray-700 text-2xl"
                >
                  ×
                </button>
              </div>

              <div className="mb-4 p-4 bg-gray-50 rounded-lg">
                <div className="grid grid-cols-2 gap-4 text-sm">
                  <div>
                    <span className="font-semibold">Total Frozen:</span> {selectedProductFrozen.frozen_quantity || 0}
                  </div>
                  <div>
                    <span className="font-semibold">Available:</span> {selectedProductFrozen.available_quantity || 0}
                  </div>
                  <div>
                    <span className="font-semibold">Total Stock:</span> {selectedProductFrozen.inventory || 0}
                  </div>
                  <div>
                    <span className="font-semibold">Freeze Count:</span> {selectedProductFrozen.frozen_details?.count || 0}
                  </div>
                </div>
              </div>

              {selectedProductFrozen.frozen_details?.all_frozen_quantities &&
                selectedProductFrozen.frozen_details.all_frozen_quantities.length > 0 ? (
                <div>
                  <h3 style={{ fontSize: '18px', fontWeight: '600', marginBottom: '16px' }}>Frozen Quantity Details</h3>
                  <div style={{
                    background: '#fff',
                    borderRadius: '12px',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
                    overflow: 'hidden'
                  }}>
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{
                        width: '100%',
                        borderCollapse: 'collapse'
                      }}>
                        <thead>
                          <tr style={{
                            background: '#f8f9fa',
                            borderBottom: '1px solid #e9ecef'
                          }}>
                            <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>ID</th>
                            <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Quantity</th>
                            <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Frozen At</th>
                            <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Expires At</th>
                            <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>User</th>
                            <th style={{ padding: '16px', textAlign: 'left', fontWeight: '600' }}>Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {selectedProductFrozen.frozen_details.all_frozen_quantities.map((fq, index) => {
                            const isExpired = new Date(fq.expires_at) < new Date();
                            const isExpiringSoon = new Date(fq.expires_at) < new Date(Date.now() + 24 * 60 * 60 * 1000); // 24 hours

                            return (
                              <tr key={fq.id} style={{
                                borderBottom: '1px solid #f1f3f4',
                                background: isExpired ? '#fef2f2' : isExpiringSoon ? '#fffbeb' : 'transparent'
                              }}>
                                <td style={{ padding: '16px' }}>
                                  <div style={{ fontSize: '12px', color: '#666' }}>{fq.id}</div>
                                </td>
                                <td style={{ padding: '16px' }}>
                                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#333' }}>{fq.quantity}</div>
                                </td>
                                <td style={{ padding: '16px' }}>
                                  <div style={{ fontSize: '12px', color: '#666' }}>
                                    {new Date(fq.frozen_at).toLocaleDateString()} {new Date(fq.frozen_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                  </div>
                                </td>
                                <td style={{ padding: '16px' }}>
                                  {editingExpiryId === fq.id ? (
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                      <input
                                        type="datetime-local"
                                        value={editingExpiryDate}
                                        onChange={(e) => setEditingExpiryDate(e.target.value)}
                                        style={{
                                          padding: '8px',
                                          border: '1px solid #d1d5db',
                                          borderRadius: '4px',
                                          fontSize: '12px'
                                        }}
                                      />
                                      <div style={{ display: 'flex', gap: '4px' }}>
                                        <button
                                          onClick={handleSaveExpiryDate}
                                          style={{
                                            padding: '4px 8px',
                                            fontSize: '10px',
                                            backgroundColor: '#10b981',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          Save
                                        </button>
                                        <button
                                          onClick={handleCancelEdit}
                                          style={{
                                            padding: '4px 8px',
                                            fontSize: '10px',
                                            backgroundColor: '#6b7280',
                                            color: 'white',
                                            border: 'none',
                                            borderRadius: '4px',
                                            cursor: 'pointer'
                                          }}
                                        >
                                          Cancel
                                        </button>
                                      </div>
                                    </div>
                                  ) : (
                                    <div>
                                      <div style={{
                                        fontSize: '12px',
                                        color: isExpired ? '#dc2626' : isExpiringSoon ? '#d97706' : '#666',
                                        fontWeight: isExpired || isExpiringSoon ? '600' : '400'
                                      }}>
                                        {new Date(fq.expires_at).toLocaleDateString()} {new Date(fq.expires_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                      </div>
                                      {isExpired && <div style={{ fontSize: '10px', color: '#dc2626', fontWeight: '500' }}>EXPIRED</div>}
                                      {isExpiringSoon && !isExpired && <div style={{ fontSize: '10px', color: '#d97706', fontWeight: '500' }}>EXPIRING SOON</div>}
                                      <div style={{ display: 'flex', gap: 6, marginTop: 6 }}>
                                        <button
                                          onClick={() => openExtendModal(fq)}
                                          style={{
                                            padding: '3px 8px', fontSize: '10px', backgroundColor: '#111', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer'
                                          }}
                                        >
                                          Extend
                                        </button>
                                        <button
                                          onClick={() => openModifyModal(fq)}
                                          style={{
                                            padding: '3px 8px', fontSize: '10px', backgroundColor: '#374151', color: '#fff', border: 'none', borderRadius: '4px', cursor: 'pointer'
                                          }}
                                        >
                                          Modify
                                        </button>
                                      </div>
                                    </div>
                                  )}
                                </td>
                                <td style={{ padding: '16px' }}>
                                  <div>
                                    <div style={{ fontSize: '14px', fontWeight: '500', color: '#333' }}>{fq.user_name || 'Guest User'}</div>
                                    {fq.user_email && (
                                      <div style={{ fontSize: '12px', color: '#666' }}>{fq.user_email}</div>
                                    )}
                                    {fq.user_id && (
                                      <div style={{ fontSize: '11px', color: '#9ca3af' }}>ID: {fq.user_id}</div>
                                    )}
                                  </div>
                                </td>
                                <td style={{ padding: '16px' }}>
                                  <span style={{
                                    padding: '4px 8px',
                                    fontSize: '12px',
                                    fontWeight: '500',
                                    borderRadius: '12px',
                                    background: isExpired ? '#fecaca' : isExpiringSoon ? '#fed7aa' : '#dcfce7',
                                    color: isExpired ? '#dc2626' : isExpiringSoon ? '#d97706' : '#166534'
                                  }}>
                                    {isExpired ? 'Expired' : isExpiringSoon ? 'Expiring Soon' : 'Active'}
                                  </span>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* View More Button */}
                  <div style={{ marginTop: '20px', textAlign: 'center' }}>
                    <button
                      onClick={() => {
                        setIsFrozenModalOpen(false);
                        navigate('/admin/freeze-history');
                      }}
                      style={{
                        background: '#333',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '12px 24px',
                        fontSize: '14px',
                        fontWeight: '600',
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.background = '#555';
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.background = '#333';
                      }}
                    >
                      <FaEye size={14} />
                      View Full History
                    </button>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  No frozen quantities found for this product.
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Extend Modal */}
      {isExtendModalOpen && extendTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-5">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Extend Expiry</h3>
                <button onClick={() => setIsExtendModalOpen(false)} className="text-gray-500 hover:text-gray-700 text-xl">×</button>
              </div>
              <div className="mb-4 text-sm text-gray-600">Frozen ID: {extendTarget.id}</div>
              <label className="block text-sm font-medium text-gray-700 mb-1">New expiry</label>
              <input type="datetime-local" value={extendDate} onChange={e => setExtendDate(e.target.value)} className="w-full p-2 border rounded focus:ring-black focus:border-black" />
              <div className="flex gap-3 mt-5">
                <button onClick={() => setIsExtendModalOpen(false)} className="flex-1 px-4 py-2 border rounded text-gray-700 hover:bg-gray-50">Cancel</button>
                <button onClick={saveExtend} className="flex-1 px-4 py-2 bg-black text-white rounded hover:bg-gray-900">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modify Modal */}
      {isModifyModalOpen && modifyTarget && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg w-full max-w-md">
            <div className="p-5">
              <div className="flex justify-between items-center mb-4">
                <h3 className="text-lg font-semibold">Modify Quantity</h3>
                <button onClick={() => setIsModifyModalOpen(false)} className="text-gray-500 hover:text-gray-700 text-xl">×</button>
              </div>
              <div className="mb-4 text-sm text-gray-600">Frozen ID: {modifyTarget.id}</div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Quantity</label>
              <input type="number" min="1" value={modifyQty} onChange={e => setModifyQty(e.target.value)} className="w-full p-2 border rounded focus:ring-black focus:border-black" />
              <div className="flex gap-3 mt-5">
                <button onClick={() => setIsModifyModalOpen(false)} className="flex-1 px-4 py-2 border rounded text-gray-700 hover:bg-gray-50">Cancel</button>
                <button onClick={saveModify} className="flex-1 px-4 py-2 bg-black text-white rounded hover:bg-gray-900">Save</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminProducts;
